/**
 * The ONE notification destination resolver (N1 destination contract, task W1).
 *
 * Every notification surface - the in-app Notifications rows (W2), the
 * service-worker notificationclick relay (W3), and the native push tap
 * handler (W4) - resolves "which in-app path does this notification open"
 * through this single function. No surface may hand-build destination paths.
 *
 * PURE by design: no React, no navigation, no analytics, no logging in here.
 * Side effects belong to CALLERS: when `known` comes back false, the caller
 * fires `logger.error` plus the ANALYTICS_EVENTS.notification_unknown_type
 * event (see src/lib/analytics-events.ts) exactly ONCE per encounter site,
 * then navigates to the returned fallback path.
 *
 * Registry parity: ./destinations.json is a vendored copy of the backend's
 * notification_destinations.json (authored in task B1) - the shared source of
 * truth mapping each push-capable or in-app-visible trigger_key to a destination
 * `kind` plus its `refs` preference order. Re-vendor it from the backend when the
 * contract changes rather than hand-editing it.
 *
 * ONE documented divergence, ahead of the backend copy: `join_space_approved`.
 * That cron lane ships an approved member their space (its action_metadata
 * carries space_slug + space_id) but is in neither registry, so every such row
 * self-reported as registry drift on each mount - a logger.error into the exact
 * Sentry channel that exists to catch a missing re-vendor - and its tap
 * dead-ended on /notifications. Keep the entry when re-vendoring, until the
 * backend copy carries it too.
 *
 * Ref semantics: `refs` arrive as flattened scalars (strings). For entity
 * kinds, refs are consumed in registry array order - the first `*_slug` ref
 * with a value becomes the slug, the first `*_id` ref with a value becomes the
 * id, and the routes.* factories (getSlugOrId semantics) prefer slug over id.
 * Refs with other suffixes (e.g. invitation_token) are kind-specific extras,
 * never path segments.
 *
 * Legacy in-app rows that predate trigger_key carry the types {me_too, uplift,
 * i_can_help, question_asked_for_you, new_member_request, space_invite,
 * we_met_request, we_met_confirmed}. Each IS a registry key, so they pass
 * through the same lookup with no alias table; any other unregistered type
 * resolves unknown.
 */

import { routes } from '@routes';
import { CommunityTab } from '@screens/CommunityDetail.tabs';
import { isPathBuildable } from '@utils/slugHelpers';

import registry from './destinations.json';

/** Flattened notification context scalars, keyed by registry ref name. */
export type NotificationRefs = Record<string, string | undefined>;

/** A resolved notification destination. */
export interface NotificationDestination {
  /** In-app path to navigate - always safe by construction. */
  path: string;
  /** False when the type is unregistered or its refs cannot build a path. */
  known: boolean;
  /**
   * The registry kind of the type - present whenever the type is registered,
   * even when degraded refs force the fallback path (the notification's kind
   * is still a fact about it). Absent for unregistered/missing types.
   * Consumers like the notifications_clicked analytics derive their
   * target_type vocabulary from it; navigation must only ever use `path`.
   */
  kind?: string;
}

interface DestinationEntry {
  kind: string;
  refs: string[];
}

/** Structural mirrors of routes.ts's private SlugEntity / ReturnToOptions. */
interface RouteEntity {
  id: string | null;
  slug?: string | null;
}

type EntityRoute = (
  entity: RouteEntity,
  opts: { returnTo: string; query?: Record<string, string> },
) => string;

const DESTINATIONS: Record<string, DestinationEntry> = registry.destinations;

/** Own-property lookup so payload-controlled types like 'constructor' never
 *  resolve through the object prototype. */
const lookupDestination = (type: string): DestinationEntry | undefined =>
  Object.prototype.hasOwnProperty.call(DESTINATIONS, type) ? DESTINATIONS[type] : undefined;

/** Normalize a flattened ref scalar: trimmed, with empty/whitespace-only
 *  values counting as absent (mirrors slugHelpers' sanitizeSegment). */
const cleanRef = (value: string | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const unknownTarget = (): { path: string; known: boolean } => ({
  path: routes.notifications(),
  known: false,
});

/**
 * Extract the route entity from the refs the registry entry declares, in
 * registry array order: first `*_slug` ref with a value wins the slug slot,
 * first `*_id` ref with a value wins the id slot. getSlugOrId inside the
 * routes.* factories then prefers slug over id (slug-first, id-fallback).
 */
function entityFromRefs(
  entry: DestinationEntry,
  refs: NotificationRefs,
): { id: string | null; slug: string | null } {
  let slug: string | null = null;
  let id: string | null = null;
  for (const refName of entry.refs) {
    const value = cleanRef(refs[refName]);
    if (!value) continue;
    if (refName.endsWith('_slug')) {
      slug ??= value;
    } else if (refName.endsWith('_id')) {
      id ??= value;
    }
  }
  return { id, slug };
}

/** Resolve an entity-backed kind through its routes.* factory, guarded by
 *  isPathBuildable so degraded refs fall back instead of throwing. */
function entityTarget(
  route: EntityRoute,
  entry: DestinationEntry,
  refs: NotificationRefs,
  returnTo: string,
  query?: Record<string, string>,
): { path: string; known: boolean } {
  const entity = entityFromRefs(entry, refs);
  if (!isPathBuildable(entity)) return unknownTarget();
  return { path: route(entity, query ? { returnTo, query } : { returnTo }), known: true };
}

/**
 * Resolve a notification's in-app destination.
 *
 * @param type - the notification's trigger_key (or legacy in-app type).
 * @param refs - flattened context scalars keyed by registry ref name.
 * @param opts.returnTo - back-navigation context appended to entity routes;
 *   defaults to '/notifications'. The bare /notifications fallback form never
 *   carries a returnTo.
 * @param opts.unknownFallback - the path a `known: false` result navigates.
 *   Defaults to '/notifications' (harmless for the in-app hub, which stays
 *   put). Push-origin callers whose viewer may be signed out pass the
 *   guest-safe home (NOTIFICATION_TAP_FALLBACK) because '/notifications' is
 *   ProtectedRoute-wrapped and renders null when signed out.
 * @returns `known: true` with the destination path when the type is registered
 *   and a path could be built; `{ path: <unknownFallback>, known: false }` for
 *   unknown types, unregistered kinds, or unbuildable refs. The path is always
 *   safe to navigate. Registered types additionally carry their registry
 *   `kind` (see NotificationDestination). Callers own the known=false side
 *   effects (logger.error + the notification_unknown_type analytics event,
 *   once per encounter site).
 */
export function resolveNotificationTarget(
  type: string | null | undefined,
  refs: NotificationRefs,
  opts?: { returnTo?: string; unknownFallback?: string },
): NotificationDestination {
  // `||` (not `??`): an explicit empty string must not bypass the default.
  const returnTo = opts?.returnTo || routes.notifications();
  const unknownFallback = opts?.unknownFallback?.trim() || routes.notifications();
  const entry = type ? lookupDestination(type) : undefined;
  if (!entry) return { path: unknownFallback, known: false };
  const resolved = resolveRegisteredTarget(entry, refs, returnTo);
  // Carry the registry `kind` even when refs degraded to the fallback (the
  // notification's kind is still a fact about it); only the path falls back.
  return resolved.known
    ? { ...resolved, kind: entry.kind }
    : { path: unknownFallback, known: false, kind: entry.kind };
}

/** Resolve a registered entry onto its kind's canonical route. */
function resolveRegisteredTarget(
  entry: DestinationEntry,
  refs: NotificationRefs,
  returnTo: string,
): { path: string; known: boolean } {
  switch (entry.kind) {
    case 'chat':
      return entityTarget(routes.chat, entry, refs, returnTo);
    case 'question':
      return entityTarget(routes.question.details, entry, refs, returnTo);
    case 'profile':
      return entityTarget(routes.profile.view, entry, refs, returnTo);
    case 'event':
      return entityTarget(routes.event.details, entry, refs, returnTo);
    case 'community_invite': {
      const invitationToken = cleanRef(refs.invitation_token);
      return entityTarget(
        routes.community.details,
        entry,
        refs,
        returnTo,
        invitationToken ? { invitation: invitationToken } : undefined,
      );
    }
    case 'community_members':
      return entityTarget(routes.community.details, entry, refs, returnTo, {
        tab: CommunityTab.Members,
      });
    case 'notifications':
      return { path: routes.notifications(), known: true };
    default:
      // A kind this resolver version does not know: the vendored registry is
      // ahead of the code. Degrade exactly like an unknown type.
      return unknownTarget();
  }
}
